import {Injectable, NgZone} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {ScriptLoaderService} from '../script-loader-service.service';
import {Observable, Subject} from 'rxjs';
import {finalize, take} from 'rxjs/operators';

export interface AppleAuthOptions {
    clientId: string;
    redirectUri: string;
    buttonId: string;
}

export interface AppleAuthResult {
    token?: string;
    error?: unknown;
}

@Injectable({providedIn: 'root'})
export class AppleAuthService {
    private readonly stateStorageKey = 'apple_oauth_state';
    private readonly nonceStorageKey = 'apple_oauth_nonce';
    private initialized = false;
    private authInProgress = false;
    private successHandler?: EventListener;
    private failureHandler?: EventListener;
    private buttonHandler?: EventListener;
    private buttonElement?: HTMLElement;
    private currentOptions?: AppleAuthOptions;
    private resultSubject = new Subject<AppleAuthResult>();

    constructor(
        private readonly scriptLoaderService: ScriptLoaderService,
        private readonly authenticationService: AuthenticationService,
        private readonly ngZone: NgZone
    ) {}

    initialize(options: AppleAuthOptions): Observable<AppleAuthResult> {
        if (!options.clientId || !options.redirectUri) {
            console.warn('Apple sign-in disabled: missing client configuration.');
            return this.resultSubject.asObservable();
        }

        this.currentOptions = options;

        if (this.initialized) {
            return this.resultSubject.asObservable();
        }

        this.scriptLoaderService.loadAppleScript()
            .then(() => this.configureApple())
            .catch((error) => {
                console.error('Apple script failed to load', error);
                this.emitError(error);
            });

        return this.resultSubject.asObservable();
    }

    dispose(): void {
        if (this.successHandler) {
            window.removeEventListener('AppleIDSignInOnSuccess', this.successHandler);
        }
        if (this.failureHandler) {
            window.removeEventListener('AppleIDSignInOnFailure', this.failureHandler);
        }
        if (this.buttonElement && this.buttonHandler) {
            this.buttonElement.removeEventListener('click', this.buttonHandler);
        }
        this.successHandler = undefined;
        this.failureHandler = undefined;
        this.buttonHandler = undefined;
        this.buttonElement = undefined;
        this.authInProgress = false;
        this.initialized = false;
    }

    private configureApple(): void {
        const options = this.currentOptions;
        const appleApi: any = (window as any).AppleID;
        if (!options) {
            this.emitError(new Error('Apple sign-in options missing.'));
            return;
        }
        if (!appleApi || !appleApi.auth || typeof appleApi.auth.init !== 'function') {
            this.emitError(new Error('AppleID SDK not available.'));
            return;
        }

        if (!(window.crypto && typeof window.crypto.getRandomValues === 'function')) {
            this.emitError(new Error('Apple sign-in unavailable: crypto API not supported.'));
            return;
        }

        this.successHandler = (event: Event) => this.handleAuthorization(event);
        this.failureHandler = (event: Event) => {
            const detail = (event as CustomEvent)?.detail || event;
            this.handleFailure(detail);
        };

        window.addEventListener('AppleIDSignInOnSuccess', this.successHandler);
        window.addEventListener('AppleIDSignInOnFailure', this.failureHandler);

        const element = document.getElementById(options.buttonId);
        if (element instanceof HTMLElement) {
            this.buttonElement = element;

            // Apple nécessite un init avant renderButton
            this.prepareAppleConfig(appleApi, options);

            if (typeof appleApi.auth.renderButton === 'function') {
                appleApi.auth.renderButton(element, {
                    type: 'sign-in',
                    color: 'black',
                    border: true,
                    width: 250,
                    height: 40
                });
            }

            if (!element.dataset.appleBound) {
                this.buttonHandler = () => this.startSignIn(appleApi);
                element.dataset.appleBound = '1';
                element.addEventListener('click', this.buttonHandler);
            }
        } else {
            this.emitError(new Error(`Apple sign-in button #${options.buttonId} not found.`));
            return;
        }

        this.initialized = true;
    }

    private startSignIn(appleApi: any): void {
        if (!this.currentOptions) {
            this.emitError(new Error('Apple sign-in options missing.'));
            return;
        }

        this.prepareAppleConfig(appleApi, this.currentOptions);

        appleApi.auth.signIn()
            .then((response: any) => this.handleAuthorization(response))
            .catch((error: unknown) => this.handleFailure(error));
    }

    private prepareAppleConfig(appleApi: any, options: AppleAuthOptions): void {
        const state = this.base64Url(window.crypto.getRandomValues(new Uint8Array(16)));
        const nonce = this.base64Url(window.crypto.getRandomValues(new Uint8Array(16)));
        sessionStorage.setItem(this.stateStorageKey, state);
        sessionStorage.setItem(this.nonceStorageKey, nonce);

        appleApi.auth.init({
            clientId: options.clientId,
            scope: 'name email',
            redirectURI: options.redirectUri,
            state,
            nonce,
            usePopup: true
        });
    }

    private handleAuthorization(result: any): void {
        this.ngZone.run(() => {
            try {
                const possibleEvent = result as CustomEvent;
                const detail = possibleEvent && typeof possibleEvent.detail !== 'undefined'
                    ? possibleEvent.detail
                    : result || {};
                const authorization = detail?.authorization ?? result?.authorization ?? {};
                const returnedState = authorization.state ?? detail?.state ?? null;
                const code = authorization.code ?? null;
                const idToken = authorization.id_token ?? authorization.idToken ?? null;
                const storedState = sessionStorage.getItem(this.stateStorageKey);
                const storedNonce = sessionStorage.getItem(this.nonceStorageKey) || '';

                if (storedState && returnedState && storedState !== returnedState) {
                    throw new Error('Apple sign-in state mismatch.');
                }

                if (!code && !idToken) {
                    throw new Error('Apple sign-in missing authorization data.');
                }

                if (this.authInProgress) {
                    console.warn('Apple sign-in already processing.');
                    return;
                }

                this.authInProgress = true;
                const payload = idToken
                    ? {code, id_token: idToken, nonce: storedNonce}
                    : {code, nonce: storedNonce};

                this.authenticationService
                    .loginWithApple(payload)
                    .pipe(
                        take(1),
                        finalize(() => {
                            this.authInProgress = false;
                            this.clearAppleSessionStorage();
                        })
                    )
                    .subscribe({
                        next: (response) => {
                            if (response?.token) {
                                this.resultSubject.next({token: response.token});
                            } else {
                                this.emitError(new Error('Apple login: backend returned no token'));
                            }
                        },
                        error: (error) => {
                            this.emitError(error);
                        }
                    });
            } catch (error) {
                this.emitError(error);
                this.authInProgress = false;
                this.clearAppleSessionStorage();
            }
        });
    }

    private handleFailure(detail: unknown): void {
        this.ngZone.run(() => {
            this.emitError(detail);
            this.authInProgress = false;
            this.clearAppleSessionStorage();
        });
    }

    private emitError(error: unknown): void {
        console.error('Apple sign-in error', error);
        this.resultSubject.next({error});
    }

    private clearAppleSessionStorage(): void {
        sessionStorage.removeItem(this.stateStorageKey);
        sessionStorage.removeItem(this.nonceStorageKey);
    }

    private base64Url(bytes: Uint8Array): string {
        const stringValue = btoa(String.fromCharCode(...Array.from(bytes)));
        return stringValue.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    }
}
